import { IOrder, IOrderDetails, IOrderShort, IRatedOrder, TripStatus } from "../../models/order/response";

/**
 * Check if an order is in a trip
 * @param order - Order object
 * @returns boolean - true if order is in a trip, false otherwise
 */
export const isOrderInTrip = (
  order: IOrder | IOrderDetails | IOrderShort | IRatedOrder
): boolean => {
  return order.tripId !== null && order.tripId !== undefined;
};

/**
 * Check if an order is in an active trip
 * @param order - Order object
 * @returns boolean - true if order is in an active trip, false otherwise
 */
export const isOrderInActiveTrip = (
  order: IOrder | IOrderDetails | IOrderShort | IRatedOrder
): boolean => {
  return isOrderInTrip(order) && order.tripStatus === TripStatus.ACTIVE;
};

/**
 * Check if an order is in a pending trip
 * @param order - Order object
 * @returns boolean - true if order is in a pending trip, false otherwise
 */
export const isOrderInPendingTrip = (
  order: IOrder | IOrderDetails | IOrderShort | IRatedOrder
): boolean => {
  return isOrderInTrip(order) && order.tripStatus === TripStatus.PENDING;
};

/**
 * Get trip status label
 * @param status - Trip status enum value
 * @returns string - Trip status label key for translation
 */
export const getTripStatusLabel = (status: TripStatus | null | undefined): string => {
  switch (status) {
    case TripStatus.PENDING:
      return "trip_pending";
    case TripStatus.ACTIVE:
      return "trip_active";
    case TripStatus.COMPLETED:
      return "trip_completed";
    case TripStatus.CANCELLED:
      return "trip_cancelled";
    default:
      return "trip_unknown";
  }
};

/**
 * Get trip status color for Tag component
 * @param status - Trip status enum value
 * @returns string - Ant Design color name
 */
export const getTripStatusColor = (status: TripStatus | null | undefined): string => {
  switch (status) {
    case TripStatus.PENDING:
      return "orange";
    case TripStatus.ACTIVE:
      return "green";
    case TripStatus.COMPLETED:
      return "blue";
    case TripStatus.CANCELLED:
      return "red";
    default:
      return "default";
  }
};

/**
 * Filter orders that are in trips
 * @param orders - Array of orders
 * @returns Array of orders that are in trips
 */
export const filterOrdersInTrip = <T extends IOrder | IOrderDetails | IOrderShort | IRatedOrder>(
  orders: T[]
): T[] => {
  return orders.filter(order => isOrderInTrip(order));
};

/**
 * Filter orders that are NOT in trips
 * @param orders - Array of orders
 * @returns Array of orders that are not in trips
 */
export const filterOrdersNotInTrip = <T extends IOrder | IOrderDetails | IOrderShort | IRatedOrder>(
  orders: T[]
): T[] => {
  return orders.filter(order => !isOrderInTrip(order));
};

/**
 * Filter orders by trip ID
 * @param orders - Array of orders
 * @param tripId - Trip ID to filter by
 * @returns Array of orders in the specified trip
 */
export const filterOrdersByTripId = <T extends IOrder | IOrderDetails | IOrderShort | IRatedOrder>(
  orders: T[],
  tripId: number
): T[] => {
  return orders.filter(order => order.tripId === tripId);
};

/**
 * Filter orders by trip status
 * @param orders - Array of orders
 * @param status - Trip status to filter by
 * @returns Array of orders with the specified trip status
 */
export const filterOrdersByTripStatus = <T extends IOrder | IOrderDetails | IOrderShort | IRatedOrder>(
  orders: T[],
  status: TripStatus
): T[] => {
  return orders.filter(order => order.tripStatus === status);
};

/**
 * Group orders by trip ID
 * @param orders - Array of orders
 * @returns Map of trip ID to array of orders
 */
export const groupOrdersByTrip = <T extends IOrder | IOrderDetails | IOrderShort | IRatedOrder>(
  orders: T[]
): Map<number, T[]> => {
  const grouped = new Map<number, T[]>();

  orders.forEach(order => {
    if (isOrderInTrip(order) && order.tripId !== null) {
      const tripOrders = grouped.get(order.tripId) || [];
      tripOrders.push(order);
      grouped.set(order.tripId, tripOrders);
    }
  });

  return grouped;
};

/**
 * Group orders by trip status
 * @param orders - Array of orders
 * @returns Map of trip status to array of orders
 */
export const groupOrdersByTripStatus = <T extends IOrder | IOrderDetails | IOrderShort | IRatedOrder>(
  orders: T[]
): Map<TripStatus, T[]> => {
  const grouped = new Map<TripStatus, T[]>();

  orders.forEach(order => {
    if (isOrderInTrip(order) && order.tripStatus !== null && order.tripStatus !== undefined) {
      const statusOrders = grouped.get(order.tripStatus) || [];
      statusOrders.push(order);
      grouped.set(order.tripStatus, statusOrders);
    }
  });

  return grouped;
};

/**
 * Get all unique trip IDs from orders
 * @param orders - Array of orders
 * @returns Array of unique trip IDs
 */
export const getUniqueTripIds = <T extends IOrder | IOrderDetails | IOrderShort | IRatedOrder>(
  orders: T[]
): number[] => {
  const tripIds = new Set<number>();

  orders.forEach(order => {
    if (isOrderInTrip(order) && order.tripId !== null) {
      tripIds.add(order.tripId);
    }
  });

  return Array.from(tripIds);
};

/**
 * Get trip summary information
 * @param orders - Array of orders
 * @param tripId - Trip ID
 * @returns Object with trip summary information
 */
export const getTripSummary = <T extends IOrder | IOrderDetails | IOrderShort | IRatedOrder>(
  orders: T[],
  tripId: number
) => {
  const tripOrders = filterOrdersByTripId(orders, tripId);

  if (tripOrders.length === 0) {
    return null;
  }

  const firstOrder = tripOrders[0];

  return {
    tripId,
    tripNumber: firstOrder.tripNumber,
    tripStatus: firstOrder.tripStatus,
    ordersCount: tripOrders.length,
    orders: tripOrders,
  };
};

/**
 * Check if order can be modified (not in active trip)
 * @param order - Order object
 * @returns boolean - true if order can be modified, false otherwise
 */
export const canModifyOrder = (
  order: IOrder | IOrderDetails | IOrderShort | IRatedOrder
): boolean => {
  return !isOrderInActiveTrip(order);
};
